/**
 * Formats a Brazilian CEP in the format XXXXX-XXX
 * Exemplos:
 * - "18078645" -> "18078-645"
 * - "18.078-645" -> "18078-645"
 * - "18078" -> "18078"
 * - "1" -> "1"
 * @param input - CEP value (string or number)
 * @returns Formatted CEP as "XXXXX-XXX" when possible
 */
import { parseDigits } from './formatters';

export const formatCEP = (input: string | number): string => {
  const digits = parseDigits(String(input));

  // Limits to a maximum of 8 digits (CEP standard)
  const clean = digits.slice(0, 8);

  // If there are 5 or less digits, returns as is (no hyphen)
  if (clean.length <= 5) return clean;

  // Applies the XXXXX-XXX mask when there are more than 5 digits
  return `${clean.slice(0, 5)}-${clean.slice(5)}`;
};

/**
 * Formats the state to uppercase
 * @param state - State to be formatted
 * @returns State in uppercase
 */
export const formatState = (state: string): string => {
  return state.trim().toUpperCase();
};

/**
 * Removes the trailing administrative qualifier that CEP providers append to
 * neighborhood/street names
 * Exemplos:
 * - "Taguatinga Centro (Taguatinga)" -> "Taguatinga Centro"
 * - "Centro" -> "Centro"
 * - "(Zona Rural)" -> "(Zona Rural)" (kept, stripping would leave nothing valid)
 * @param value - Neighborhood or street returned by the CEP provider
 * @returns Value without the trailing parenthesized qualifier
 */
export const stripLocationQualifier = (value: string): string => {
  const stripped = value.replace(/\s*\([^()]*\)\s*$/, '').trim();
  return stripped.length >= 3 ? stripped : value.trim();
};

/**
 * Clears and formats the address number
 * @param number - Address number
 * @returns Formatted number
 */
export const formatAddressNumber = (number: string): string => {
  const trimmed = number.trim();
  const isSN = /^s\/n$/i.test(trimmed);
  if (isSN) return 'S/N';
  return trimmed;
};
